import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, PencilSimpleIcon, TrashIcon, TagIcon } from "@phosphor-icons/react";

export default function ExpenseCategoriesPage() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/expense-categories");
      setItems(r.data);
    } catch { toast.error("Failed to load categories"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="expense-categories-page">
      <div className="flex items-end justify-between border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Settings</div>
          <h1 className="font-display text-4xl font-black">Expense Categories</h1>
          <p className="text-slate-500 mt-2">Add, rename or remove the labels that group your shop's expenses.</p>
        </div>
        <AddDialog onDone={load} />
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="uppercase text-xs tracking-wider">Category</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <CategoryRow key={c.id} c={c} onDone={load} />
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={2} className="text-center text-slate-400 py-10">No categories yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CategoryRow({ c, onDone }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);

  const save = async () => {
    if (!name.trim() || name === c.name) { setEditing(false); return; }
    try {
      await api.patch(`/expense-categories/${c.id}`, { name: name.trim() });
      toast.success("Renamed");
      setEditing(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/expense-categories/${c.id}`);
      toast.success("Deleted");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <TableRow className="hover:bg-slate-50 border-b border-slate-100" data-testid={`category-row-${c.id}`}>
      <TableCell className="font-semibold">
        {editing ? (
          <div className="flex gap-2 items-center">
            <TagIcon size={14} className="text-slate-400" />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setName(c.name); } }}
              autoFocus
              className="rounded-none h-8 w-64"
              data-testid={`category-rename-input-${c.id}`}
            />
            <Button size="sm" onClick={save} className="rounded-none h-8 bg-slate-900 hover:bg-black" data-testid={`category-save-${c.id}`}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setName(c.name); }} className="rounded-none h-8">Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <TagIcon size={14} className="text-slate-400" />
            <span>{c.name}</span>
          </div>
        )}
      </TableCell>
      <TableCell className="text-right">
        {!editing && (
          <div className="inline-flex gap-2">
            <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-900" data-testid={`category-edit-${c.id}`}>
              <PencilSimpleIcon size={16} />
            </button>
            <button onClick={remove} className="text-slate-400 hover:text-[#FF2400]" data-testid={`category-delete-${c.id}`}>
              <TrashIcon size={16} />
            </button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

function AddDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expense-categories", { name: name.trim() });
      toast.success("Category added");
      setOpen(false); setName("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-slate-900 hover:bg-black text-white" data-testid="add-category-button">
          <PlusIcon size={16} className="mr-2" /> Add category
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display">New category</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-category-form">
          <div>
            <Label className="overline">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Electricity, Rent, Fuel"
              required
              maxLength={40}
              className="rounded-none"
              data-testid="category-name-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="category-submit-button">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
